import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../../assets/icons/eye-fill-icon.svg';
import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ApiRequestRejectedInterface,
  RequestRejectedInterface,
} from '../../../../interface/request-rejected-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  disableRejectedFilterApplied,
  fetchRequestRejected,
  fetchRequestRejectedFilters,
  updateRejectedFilters,
  setSortData,
} from '../../../../redux/slice/RequestRejectedSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import penaltyManagementApi from '../../../../utils/api/penalty-management';

import ViewOverrides from './ViewOverrides';

interface ModelDetailsInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
  position?: string;
}

/**
 * Request rejected component
 * @returns
 */
function RequestRejected() {
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [isSidePaneOpen, setIsSidePaneOpen] = useState<boolean>(false);
  const [SidePaneView, setSidePaneView] = useState<ModelDetailsInterface>({
    title: ' ',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const {
    requestRejected,
    limit,
    page,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    loading,
  } = useAppSelector((state) => state.requestRejected);

  /**
   * function for closing modal
   * @param message
   */
  const closeSidePane = (message: string) => {
    if (message === 'success') {
      setIsSidePaneOpen(false);
      setSidePaneView({
        title: ' ',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setIsSidePaneOpen(false)}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    } else {
      setIsSidePaneOpen(false);
    }
  };

  /**
   * Function for fetching request approved data
   * @param data
   */
  const fetchData = (data: ApiRequestRejectedInterface) => {
    if (!loading) {
      dispatch(fetchRequestRejected(data));
    }
  };

  /**
   * Function for refreshing request approved data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * function for opening overlay
   */
  const handleOpenSidePane = (currentData: RequestRejectedInterface) => {
    const accountDetails = requestRejected.find((value) => value._id === currentData._id);
    if (accountDetails) {
      setIsSidePaneOpen(true);
      setSidePaneView({
        title: ' ',
        header: false,
        content: (
          <ViewOverrides
            type="rejected"
            handleRefresh={handleRefresh}
            currentData={accountDetails}
            closeModal={closeSidePane}
            hasEdit
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };

  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered dynamic pricing cell
   */
  const renderDynamicPricing = (currentRow: RequestRejectedInterface) => {
    let dynamicPricingList: string;
    if (currentRow.dynamicPricing.length > 2) {
      dynamicPricingList = 'All';
    } else {
      dynamicPricingList = currentRow.dynamicPricing
        .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
        .join(', ');
    }
    return <p>{dynamicPricingList}</p>;
  };
  /**
   * Renders custom data for the Ceiling Type column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: RequestRejectedInterface) => {
    const formattedValue = currentRow.ceilingType
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (char) => char.toUpperCase());
    return <p>{formattedValue}</p>;
  };

  const headers = [
    {
      label: t('station'),
      accessorKey: 'stationNames' as keyof RequestRejectedInterface,
      sortAllow: true,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof RequestRejectedInterface,
      sortAllow: false,
      customData: renderDynamicPricing,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof RequestRejectedInterface,
      sortAllow: true,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof RequestRejectedInterface,
      sortAllow: true,
      /**
       *  ToFixedValue
       * @param currentRow
       * @returns
       */
      customData: (currentRow: RequestRejectedInterface) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(currentRow.ceilingValue)}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof RequestRejectedInterface,
      sortAllow: false,
      customData: renderCeilingType,
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof RequestRejectedInterface,
      sortAllow: true,
      /**
       * customData
       */
      customData: (customData: RequestRejectedInterface) => (
        <div>
          {ConvertUTCtoDate(customData.effectiveFrom)} - {ConvertUTCtoDate(customData.effectiveTo)}
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof RequestRejectedInterface,
      /**
       * Rendering action
       * @returns
       */
      customData: (currentData: RequestRejectedInterface) => (
        <div
          role="presentation"
          className="action-button"
          onClick={() => {
            handleOpenSidePane(currentData);
          }}>
          <EyeIcon />
        </div>
      ),
    },
  ];

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageAndLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('station'), accessorKey: 'stationNames' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('effectiveDate'), accessorKey: 'effectiveDate' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function for exporting request approved data in pdf or csv format
   * @param type
   */
  const exportDataPDFOrCSV = (type: 'pdf' | 'csv') => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    penaltyManagementApi
      .requestExport({ ...queryParameters, requestStatus: 'rejected' })
      .then((response) => {
        const header = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: RequestRejectedInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'ceilingValue':
                return `${ToFixedValue(row.ceilingValue)} ${branding.text.currency}` || 'N/A';
              case 'effectiveDate':
                return `${ConvertUTCtoDate(row.effectiveFrom)} - ${ConvertUTCtoDate(row.effectiveTo)}`;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );

        if (type === 'pdf') {
          exportPDF(header, rows, 'request-rejected');
        } else {
          exportCSV(header, rows, 'request-rejected');
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (isFilterUpdated || requestRejected.length === 0) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRejectedFilterApplied());
    }
  }, [filters]);

  return (
    <div>
      <SidePaneModel
        title={SidePaneView.title}
        // header={SidePaneView.header}
        // rightCorner={SidePaneView.rightCorner}
        content={SidePaneView.content}
        closePanel={() => setIsSidePaneOpen(false)}
        open={isSidePaneOpen}
      />
      <CustomTable
        serialNo
        header={headers}
        data={requestRejected}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        currentPage={page}
        filterListLoading={filterListLoading}
        filters={filters}
        sortData={{ sortBy, sortField }}
        loading={loading}
        handleSortChange={handleSortFunction}
        handleLimitChange={pageAndLimitChange} // ✅ REQUIRED
        handlePageChange={pageAndLimitChange}
        exportData={requestRejected}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPDFOrCSV}
        filtersToDispatch={updateRejectedFilters}
        filterListToDispatch={fetchRequestRejectedFilters}
        refreshRequired
        leftTopContent={<div />}
        sizeChangeButtonRequired
        hoverLabel={t('refresh')}
        refreshCurrentPages={handleRefresh}
        tableHeight="60vh"
        paginate
        topHeader={false}
        onHiddenColumnsChange={setHiddenColumns}
        flexDirection
      />
    </div>
  );
}

export default RequestRejected;
